// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  CohortItem,
  isEventBasedEntry,
  isInlineCohortItem,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryCohortEntry,
  QueryEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {dataGroupIDsEqual} from 'common/util/index';
import {hasSCDFiltersFromOtherDataGroup, hasUserFilters} from 'common/util/custom_events';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  isCohortSegment,
  isCohortSegmentItem,
  isEventSegment,
  isInlineCohortSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {CustomEventDefinition, CustomProperty} from 'common/report/util/saved-entities/types';
import {reporter} from 'common/report/error-reporting';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {
  isAggregateUserFreqByGroupSegmentation,
  isBehaviorSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {Segmentation} from 'common/types/reports/query';
import type {BehaviorMetric} from 'common/report/insights/util/types';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {isScdPropertyName} from 'common/report/util/properties';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {CustomProperties} from 'common/report/stores/custom-properties';
import {isScdProperty, Property} from 'common/query-builder/query-block/properties-menu/types';

import {QueryBuilderSelection} from '../types';

/**
 * Removes invalid entries for the selected data group and updates dataGroupId fields on the remaining entries
 */
export function setQueryDataGroup({
  query,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): QueryBuilderSelection {
  const sanitizedQuery = sanitizeQueryForDataGroup({
    query,
    dataGroupId,
    customEvents,
    customProperties,
    metricsResource,
    behaviorsResource,
  });
  return setQueryDataGroupProps({query: sanitizedQuery, dataGroupId});
}

function metricHasCustomBehaviorWithInvalidFilters({
  metric,
  customEvents,
  dataGroupId,
}: {
  metric: MetricEntry;
  customEvents: CustomEventDefinition[];
  dataGroupId: string;
}): boolean {
  const customEventIds = new Set<number>();

  metric.behavior.behaviors.forEach((behavior) => {
    const hasEventComparison = !!behavior.behaviors;
    if (hasEventComparison) {
      behavior.behaviors.forEach((beh) => {
        if (beh.type === MetricType.CustomEvent) {
          customEventIds.add(beh.id);
        }
      });
    }
    if (behavior.type === MetricType.CustomEvent) {
      customEventIds.add(behavior.id);
    }
  });

  if (customEventIds.size === 0) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  return Array.from(customEventIds).some((id) => {
    const customEvent = customEvents.find((cb) => cb.id === id);
    return (
      customEvent &&
      (hasUserFilters(customEvent, customEvents) ||
        hasSCDFiltersFromOtherDataGroup({
          customEvent,
          customEvents,
          dataGroupId,
        }))
    );
  });
}

function metricHasInvalidFilterForDataGroup({
  behaviors,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}) {
  return behaviors.some((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return metricHasInvalidFilterForDataGroup({
        behaviors: beh.behaviors,
        dataGroupId,
        customEvents,
        customProperties,
        metricsResource,
        behaviorsResource,
      });
    }
    return beh.filters.some((filter) =>
      filterIsInvalidForDataGroup({
        filter,
        dataGroupId,
        customEvents,
        customProperties,
        metricsResource,
        behaviorsResource,
      }),
    );
  });
}

export function isCustomEventWithUserFilters(
  eventEntry: QueryEventEntry,
  customEvents: CustomEventDefinition[],
): boolean {
  if (!eventEntry.event.custom) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  const customEvent = customEvents.find((ce) => ce.id === eventEntry.event.id);
  return !customEvent || hasUserFilters(customEvent, customEvents);
}

export function cohortEntryIsInvalidForDataGroup(item: QueryCohortEntry | CohortSegment, dataGroupId?: string) {
  if (!isCohortSegmentItem(item)) {
    item = convertCohortItemToCohortSegment(item.event);
  }
  return !dataGroupIDsEqual(getCohortSegmentDataGroupId(item), dataGroupId);
}

export function isClauseInvalidForDataGroup({
  clause,
  dataGroupId,
  customEvents,
}: {
  clause: BehaviorMetric;
  dataGroupId: string;
  customEvents: any[];
}) {
  const {type, name, id, raw_cohort: rawCohort} = clause.behavior;
  const {property} = clause.measurement;
  return (
    (type === MetricType.Cohort &&
      cohortEntryIsInvalidForDataGroup(
        {
          cohort: {
            id,
            name,
            raw_cohort: rawCohort,
          },
        },
        dataGroupId,
      )) ||
    // Detects People property ("All People" is supported by group-level analysis)
    (type === MetricType.People && property && dataGroupId) ||
    // Custom events that are based on user properties
    (dataGroupId &&
      type === MetricType.CustomEvent &&
      hasUserFilters(
        {
          name,
          id,
        } as CustomEventDefinition,
        customEvents,
      ))
  );
}

function sanitizeQueryForDataGroup({
  query,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): QueryBuilderSelection {
  return {
    ...query,
    entries: sanitizeEventsForDataGroup({
      queryEntries: query.entries,
      dataGroupId,
      customEvents,
      customProperties,
      metricsResource,
      behaviorsResource,
    }),
    exclusionSteps: sanitizeEventsForDataGroup({
      queryEntries: query.exclusionSteps,
      dataGroupId,
      customEvents,
      customProperties,
      metricsResource,
      behaviorsResource,
    }) as QueryEventEntry[],
    filters: query.filters.filter(
      (filter) =>
        !filterIsInvalidForDataGroup({
          filter,
          dataGroupId,
          customEvents,
          customProperties,
          metricsResource,
          behaviorsResource,
        }),
    ),
    groupBy: sanitizeGroupByForDataGroup({
      segments: query.groupBy,
      dataGroupId,
      customProperties,
    }),
  };
}

function sanitizeMetricBehaviorsForDataGroup({
  behaviors,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}) {
  return behaviors.map((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return {
        ...beh,
        behaviors: sanitizeMetricBehaviorsForDataGroup({
          behaviors: beh.behaviors,
          dataGroupId,
          customEvents,
          customProperties,
          metricsResource,
          behaviorsResource,
        }),
      };
    }
    const filters = beh.filters.filter(
      (filter) =>
        !filterIsInvalidForDataGroup({
          filter,
          dataGroupId,
          customEvents,
          customProperties,
          metricsResource,
          behaviorsResource,
        }),
    );
    return {
      ...beh,
      filters,
    };
  });
}

function sanitizeEventsForDataGroup({
  queryEntries,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): Array<QueryEntry | MetricEntry> {
  // remove invalid event filters and property aggregations
  const sanitizedQueryEntries = queryEntries.map((entry) => {
    if (isEventBasedEntry(entry) || isUserBasedEntry(entry)) {
      if (isMetricEntry(entry)) {
        const {behavior, measurement} = entry;
        return {
          ...entry,
          behavior: {
            ...behavior,
            behaviors: sanitizeMetricBehaviorsForDataGroup({
              behaviors: behavior.behaviors,
              dataGroupId,
              customEvents,
              customProperties,
              metricsResource,
              behaviorsResource,
            }),
          },
          measurement: {
            ...measurement,
            property: propertyIsInvalidForDataGroup(measurement.property, dataGroupId) ? null : measurement.property,
          },
        };
      }
      return {
        ...entry,
        filters: entry.filters.filter(
          (filter) =>
            !filterIsInvalidForDataGroup({
              filter,
              dataGroupId,
              customEvents,
              customProperties,
              metricsResource,
              behaviorsResource,
            }),
        ),
        property: propertyIsInvalidForDataGroup(entry.property, dataGroupId) ? null : entry.property,
      };
    } else {
      return entry;
    }
  });
  return sanitizedQueryEntries.filter((queryEntry) => {
    return !queryEntryIsInvalidForDataGroup({
      queryEntry,
      dataGroupId,
      customEvents,
      customProperties,
      metricsResource,
      behaviorsResource,
    });
  });
}

function queryEntryIsInvalidForDataGroup({
  queryEntry,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  queryEntry: QueryEntry | MetricEntry;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): boolean {
  switch (queryEntry.type) {
    case TopLevelMetricType.MetricEntry:
      return (
        (dataGroupId &&
          metricHasCustomBehaviorWithInvalidFilters({
            metric: queryEntry,
            customEvents,
            dataGroupId,
          })) ||
        (dataGroupId &&
          metricHasInvalidFilterForDataGroup({
            behaviors: queryEntry.behavior.behaviors,
            dataGroupId,
            customEvents,
            customProperties,
            metricsResource,
            behaviorsResource,
          })) ||
        (queryEntry.measurement.property && propertyIsInvalidForDataGroup(queryEntry.measurement.property, dataGroupId))
      );
    case ResourceType.Event:
    case SelectorTypeDefinitions.FlowsEvent:
    case SelectorTypeDefinitions.Behavior: {
      return (
        (dataGroupId && isCustomEventWithUserFilters(queryEntry as QueryEventEntry, customEvents)) ||
        queryEntry.filters.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            customEvents,
            customProperties,
            metricsResource,
            behaviorsResource,
          }),
        ) ||
        (queryEntry.property && propertyIsInvalidForDataGroup(queryEntry.property, dataGroupId))
      );
    }
    case ResourceType.Formula: {
      return getResolvedFormulaEntry({
        metricsResource,
        behaviorsResource,
        formulaEntry: queryEntry,
      })?.event?.referencedMetrics?.some((c) =>
        isClauseInvalidForDataGroup({clause: c as BehaviorMetric, dataGroupId, customEvents}),
      );
    }
    case SelectorTypeDefinitions.CohortGroup:
    case ResourceType.User: {
      const isAllPeople = `$all_people` === queryEntry.event.value;
      return (
        (!isAllPeople &&
          queryEntry.property &&
          profilePropertyIsInvalidForDataGroup({
            profilePropertyDataGroupId: queryEntry.dataGroupId,
            profilePropertyName: queryEntry.property.name,
            dataGroupId,
          })) ||
        queryEntry.filters.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            customEvents,
            customProperties,
            metricsResource,
            behaviorsResource,
          }),
        )
      );
    }
    case ResourceType.Cohort:
      return cohortEntryIsInvalidForDataGroup(queryEntry, dataGroupId);
    case SelectorTypeDefinitions.GroupedEvent:
      return queryEntry.event.some((entry) =>
        queryEntryIsInvalidForDataGroup({
          queryEntry: entry,
          dataGroupId,
          customEvents,
          customProperties,
          metricsResource,
          behaviorsResource,
        }),
      );
    default:
      // Log unhandled cases and default to marking as invalid for non-null data group
      reporter.error(`Unexpected QueryEntry.type: ${JSON.stringify(queryEntry)}`);
      return !!dataGroupId;
  }
}

function filterIsInvalidForDataGroup({
  filter,
  dataGroupId,
  customEvents,
  customProperties,
  metricsResource,
  behaviorsResource,
}: {
  filter: Partial<PropertyFilter>;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
  customProperties: CustomProperties;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}): boolean {
  switch (filter.resourceType) {
    case ResourceType.User: {
      if (filter.customProperty && isBehavioralCustomProperty(filter.customProperty)) {
        const {behavior} = filter.customProperty;
        return queryEntryIsInvalidForDataGroup({
          queryEntry: {
            ...behavior,
            type: SelectorTypeDefinitions.Behavior,
            filters: (behavior.filters ?? []).map((segmentation) => new PropertyFilter({...segmentation})),
          },
          dataGroupId,
          customEvents,
          customProperties,
          metricsResource,
          behaviorsResource,
        });
      }

      if (!dataGroupId && isAggregateUserFreqByGroupSegmentation(filter as PropertyFilter)) {
        return true;
      }

      // TODO remove when behaviors are no longer user resource type
      if (isBehaviorSegmentation(filter as Segmentation)) {
        return false;
      }

      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties,
        });
      }

      return profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: filter.dataGroupId,
        profilePropertyName: filter.propertyName,
        dataGroupId,
      });
    }
    case ResourceType.Cohort:
      return true;
    case ResourceType.Event:
    case ResourceType.Metric:
    case ResourceType.Other:
    case ResourceType.Formula:
      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties,
        });
      }
      return false;
  }
}

function sanitizeGroupByForDataGroup({
  segments,
  dataGroupId,
  customProperties,
}: {
  segments: Segment[];
  dataGroupId: string;
  customProperties: CustomProperties;
}): Segment[] {
  const filteredSegments = segments.filter(
    (s) =>
      !segmentItemIsInvalidForDataGroup({
        segment: s,
        dataGroupId,
        customProperties,
      }),
  );
  return filteredSegments;
}

function segmentItemIsInvalidForDataGroup({
  segment,
  dataGroupId,
  customProperties,
}: {
  segment: Segment;
  dataGroupId: string;
  customProperties: CustomProperties;
}): boolean {
  if (isUserSegment(segment) && (segment.user.customProperty || segment.user.customPropertyId)) {
    return customPropertyIsInvalidForDataGroup({
      dataGroupId,
      customProperty: segment.user.customProperty,
      customPropertyId: segment.user.customPropertyId,
      customProperties,
    });
  } else if (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)) {
    // TODO remove once behaviors are not user resource type
    return [].concat(segment).some((seg) =>
      profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: seg.user.dataGroupId,
        profilePropertyName: seg.user.propertyName,
        dataGroupId,
      }),
    );
  } else if (isCohortSegment(segment)) {
    return true;
  } else {
    // EventSegment, OtherSegment
    if (isEventSegment(segment) && (segment.event.customProperty || segment.event.customPropertyId)) {
      return customPropertyIsInvalidForDataGroup({
        dataGroupId,
        customProperty: segment.event.customProperty,
        customPropertyId: segment.event.customPropertyId,
        customProperties,
      });
    }
    return false;
  }
}

function setEntriesForDataGroup({
  queryEntries,
  dataGroupId,
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
}): Array<QueryEntry | MetricEntry> {
  return queryEntries.map((entry) => {
    if (isMetricEntry(entry)) {
      const {behavior, measurement} = entry;
      if (dataGroupId === null && measurement.property) {
        delete measurement.property[`dataGroupId`];
      }
      return {
        ...entry,
        behavior: {
          ...behavior,
          dataGroupId,
        },
        measurement,
      };
    } else {
      if (dataGroupId === null && entry.property) {
        delete entry.property[`dataGroupId`];
      }
      return {
        ...entry,
        dataGroupId,
      };
    }
  });
}

function setQueryDataGroupProps({
  query,
  dataGroupId,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
}): QueryBuilderSelection {
  return {
    ...query,
    entries: setEntriesForDataGroup({queryEntries: query.entries, dataGroupId}),
    exclusionSteps: query.exclusionSteps.map((entry) => ({...entry, dataGroupId})),
    dataGroupId,
  };
}

function customPropertyIsInvalidForDataGroup({
  dataGroupId,
  customProperty,
  customPropertyId,
  customProperties,
}: {
  dataGroupId: string;
  customProperty: CustomProperty;
  customPropertyId: string | number;
  customProperties: CustomProperties;
}) {
  function isInvalidForDataGroup(customProp: CustomProperty, customPropId: string | number) {
    const composedProperties = customProp
      ? customProp.composedProperties
      : customProperties[customPropId].composedProperties;
    return Object.values(composedProperties).some((prop: Property) => {
      if (prop.customPropertyId) {
        return isInvalidForDataGroup(prop.customProperty, prop.customPropertyId);
      }

      // In group mode, custom prop is invalid if it has user props or group props from other data groups
      if (dataGroupId) {
        return prop.resourceType === ResourceType.User && prop.dataGroupId !== dataGroupId;
      }
      // In user mode, custom prop is invalid if it has scd props from other data groups
      return isScdProperty(prop) && prop.dataGroupId !== dataGroupId;
    });
  }

  return isInvalidForDataGroup(customProperty, customPropertyId);
}

export function profilePropertyIsInvalidForDataGroup({
  profilePropertyDataGroupId,
  profilePropertyName,
  dataGroupId,
}: {
  profilePropertyDataGroupId: string;
  profilePropertyName: string | string[];
  dataGroupId: string;
}) {
  if (typeof profilePropertyName === `string` && isScdPropertyName(profilePropertyName)) {
    return profilePropertyDataGroupId !== dataGroupId;
  } else if (profilePropertyName === `$mp_first_event_time`) {
    // First event time is a behavioral property, so it can only work
    // when the data group of the query matches the data group of the property
    // if we add support for b2b behavioral properties they will need to
    // be removed here as well
    return profilePropertyDataGroupId !== dataGroupId;
  } else if (dataGroupId) {
    // B2B analysis allows using all data groups' profile properties except for the empty "user" data group
    return !profilePropertyDataGroupId;
  } else {
    // User-level analysis can use all data groups' profile properties
    return false;
  }
}

function convertCohortItemToCohortSegment(item: CohortItem): CohortSegment {
  if (isInlineCohortItem(item)) {
    return {cohort: {...item.value.cohort, negated: false}};
  } else {
    return {
      cohort: {
        id: item.value as number,
        name: item.label,
        negated: false,
        ...(item.data_group_id ? {data_group_id: item.data_group_id} : null),
      },
    };
  }
}

function getCohortSegmentDataGroupId(segment: CohortSegment): string {
  if (isInlineCohortSegment(segment)) {
    return segment.cohort.raw_cohort.data_group_id;
  } else {
    return segment.cohort.data_group_id;
  }
}

function propertyIsInvalidForDataGroup(propertyItem?: PropertySectionItem, dataGroupId?: string): boolean {
  return (
    !!propertyItem &&
    propertyItem.resourceType === ResourceType.User &&
    profilePropertyIsInvalidForDataGroup({
      profilePropertyDataGroupId: propertyItem.dataGroupId,
      profilePropertyName: propertyItem.name || propertyItem.value,
      dataGroupId,
    })
  );
}
